import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { useTranslation } from "react-i18next";
import { meetingMediaUrl } from "../../api/Meetings/meetingSessionsApi";
import {
  extractVideoTranscriptSections,
  mediaKindFromMime,
  isMeetingPdfPhoto,
  notesByPhotoId,
  audioCouplingByPhotoId,
  shortMeetingBlurb,
} from "../../utils/meetingRecorder";
import { htmlToPlainText } from "../../utils/novedadHtml";

const PRINT_STYLE_ID = "meeting-summary-print-style";
const PRINT_ROOT_ID = "meeting-summary-print";
const PRINT_IFRAME_ID = "meeting-summary-print-frame";

/** CSS del documento de impresión (iframe aislado: no tumba la app). */
const PRINT_DOC_CSS = `
  @page { margin: 14mm 16mm; size: A4; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    background: #fff;
    color: #14201d;
    font-family: "Segoe UI", "Helvetica Neue", Arial, sans-serif;
    font-size: 12px;
    line-height: 1.45;
  }
  .mp-brand {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding-bottom: 10px;
    margin-bottom: 14px;
    border-bottom: 3px solid #00c2a3;
  }
  .mp-brand-mark {
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #00c2a3;
  }
  .mp-brand-sub { font-size: 10px; color: #5a6b66; }
  .mp-title {
    font-size: 20px;
    font-weight: 800;
    line-height: 1.25;
    margin: 0 0 10px;
    color: #0f1f1b;
  }
  .mp-meta {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px 16px;
    padding: 10px 12px;
    margin: 0 0 14px;
    background: #f4faf8;
    border: 1px solid #d7e8e3;
    border-radius: 8px;
    font-size: 11px;
    color: #3d524c;
  }
  .mp-meta-row { margin: 0; }
  .mp-meta-label {
    font-weight: 700;
    color: #067a68;
    margin-right: 4px;
  }
  .mp-raw {
    font-size: 12px;
    line-height: 1.5;
    margin: 0 0 16px;
    padding: 10px 12px;
    border-left: 3px solid #00c2a3;
    background: #fafcfb;
    white-space: pre-wrap;
  }
  .mp-section { margin: 0 0 16px; }
  .mp-section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #067a68;
    margin: 0 0 8px;
    padding-bottom: 4px;
    border-bottom: 1px solid #cfe4de;
  }
  .mp-section-count {
    display: inline-block;
    min-width: 18px;
    padding: 1px 6px;
    border-radius: 999px;
    background: #00c2a3;
    color: #fff;
    font-size: 10px;
    font-weight: 800;
    text-align: center;
  }
  .mp-item {
    display: grid;
    grid-template-columns: 22px 1fr;
    gap: 6px;
    margin: 0 0 8px;
    font-size: 11px;
    line-height: 1.4;
    page-break-inside: avoid;
  }
  .mp-item-num {
    font-weight: 800;
    color: #00c2a3;
    font-size: 11px;
  }
  .mp-item-body {
    display: grid;
    grid-template-columns: 72px 1fr;
    gap: 8px;
    align-items: start;
  }
  .mp-item-body.mp-no-thumb {
    grid-template-columns: 1fr;
  }
  .mp-thumb-img {
    width: 72px;
    height: 72px;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid #cfe4de;
    background: #eef5f3;
    display: block;
  }
  .mp-thumb-fallback {
    width: 72px;
    height: 72px;
    border-radius: 6px;
    border: 1px solid #cfe4de;
    background: #e8f6f4;
    color: #067a68;
    font-size: 10px;
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .mp-item-text { white-space: pre-wrap; color: #14201d; }
  .mp-footer {
    margin-top: 18px;
    padding-top: 8px;
    border-top: 1px solid #d7e8e3;
    font-size: 9px;
    color: #6a8079;
    display: flex;
    justify-content: space-between;
  }
`;

function ensurePrintHostStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById(PRINT_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = PRINT_STYLE_ID;
  style.textContent = `
@media screen {
  #${PRINT_ROOT_ID} {
    position: absolute !important;
    left: -10000px !important;
    top: 0 !important;
    width: 210mm !important;
    pointer-events: none !important;
    opacity: 0 !important;
  }
}
#${PRINT_IFRAME_ID} {
  position: fixed !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 0 !important;
  height: 0 !important;
  border: 0 !important;
  opacity: 0 !important;
  pointer-events: none !important;
}
`;
  document.head.appendChild(style);
}

/**
 * Imprime en un iframe (texto, sin miniaturas).
 * Evita tumbar Chrome con 200+ fotos en window.print() de la app.
 */
export function printMeetingSummary() {
  if (typeof document === "undefined") return;
  ensurePrintHostStyles();
  const root = document.getElementById(PRINT_ROOT_ID);
  if (!root) {
    console.warn("[meetings] print root missing");
    return;
  }
  const docBody = root.querySelector(".mp-doc") || root;
  const html = docBody.innerHTML;
  if (!String(html || "").trim()) {
    console.warn("[meetings] print content empty");
    return;
  }

  let frame = document.getElementById(PRINT_IFRAME_ID);
  if (frame) frame.remove();
  frame = document.createElement("iframe");
  frame.id = PRINT_IFRAME_ID;
  frame.setAttribute("title", "Meeting print");
  document.body.appendChild(frame);

  const win = frame.contentWindow;
  const doc = frame.contentDocument || win?.document;
  if (!win || !doc) {
    console.warn("[meetings] print iframe unavailable");
    frame.remove();
    return;
  }

  doc.open();
  doc.write(
    `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>STAGE</title><style>${PRINT_DOC_CSS}</style></head><body>${html}</body></html>`,
  );
  doc.close();

  const cleanup = () => {
    window.setTimeout(() => {
      try {
        frame.remove();
      } catch {
        /* ignore */
      }
    }, 500);
  };

  const waitForThumbs = () => {
    const imgs = Array.from(doc.images || []);
    if (!imgs.length) return Promise.resolve();
    const pending = imgs.filter((img) => !img.complete);
    if (!pending.length) return Promise.resolve();
    return Promise.race([
      Promise.all(
        pending.map(
          (img) =>
            new Promise((resolve) => {
              img.onload = resolve;
              img.onerror = resolve;
            }),
        ),
      ),
      new Promise((resolve) => window.setTimeout(resolve, 10000)),
    ]);
  };

  const runPrint = () => {
    try {
      win.focus();
      win.print();
    } catch (err) {
      console.warn("[meetings] print failed:", err);
    } finally {
      cleanup();
    }
  };

  waitForThumbs().then(() => window.setTimeout(runPrint, 60));
}

/** Solo thumbs del back (nunca el original: tumba Chrome con cientos de fotos). */
function printThumbUrl(photo) {
  if (!photo?.thumbUrl) return "";
  return meetingMediaUrl(photo.thumbUrl);
}

function itemPhotoIds(item) {
  if (Array.isArray(item?.photoIds) && item.photoIds.length) {
    return item.photoIds.map(Number).filter((n) => Number.isFinite(n));
  }
  if (item?.photoId != null) {
    const n = Number(item.photoId);
    return Number.isFinite(n) ? [n] : [];
  }
  return [];
}

/** Sección de impresión: texto + miniatura (thumbUrl del back, no el original). */
function FlatSection({ title, items, photosById = null, showThumbs = false }) {
  if (!items?.length) return null;
  return (
    <Box className="mp-section meeting-print-section">
      <Typography component="h2" className="mp-section-title">
        {title}
        <Box component="span" className="mp-section-count">
          {items.length}
        </Box>
      </Typography>
      {items.map((item, idx) => {
        const text = typeof item === "string" ? item : item?.text;
        if (!String(text || "").trim()) return null;
        const ids = showThumbs && photosById ? itemPhotoIds(item) : [];
        const photo = ids.length ? photosById.get(Number(ids[0])) : null;
        const thumb = printThumbUrl(photo);
        const isVideo =
          photo &&
          (photo.kind === "video" ||
            mediaKindFromMime(photo.mimeType) === "video");
        const isPdf = photo && isMeetingPdfPhoto(photo);
        const hasThumbCol = showThumbs && photosById && ids.length > 0;
        return (
          <Box key={`${title}-${idx}`} className="mp-item">
            <Box component="span" className="mp-item-num">
              {idx + 1}.
            </Box>
            <Box
              className={
                hasThumbCol ? "mp-item-body" : "mp-item-body mp-no-thumb"
              }
            >
              {hasThumbCol ? (
                thumb && !isPdf ? (
                  <Box
                    component="img"
                    className="mp-thumb-img"
                    src={thumb}
                    alt=""
                    loading="eager"
                  />
                ) : (
                  <Box className="mp-thumb-fallback">
                    {isPdf ? "PDF" : isVideo ? "Vídeo" : "Foto"}
                  </Box>
                )
              ) : null}
              <Typography component="div" className="mp-item-text">
                {text}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function PrintSection({
  title,
  items,
  photosById,
  defaultExpanded = true,
  onOpenMedia,
}) {
  if (!items?.length) return null;
  return (
    <Accordion
      disableGutters
      elevation={0}
      defaultExpanded={defaultExpanded}
      sx={{
        mb: 1,
        bgcolor: "transparent",
        "&:before": { display: "none" },
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
      className="meeting-print-section meeting-screen-only"
    >
      <AccordionSummary
        expandIcon={<ExpandMoreOutlinedIcon />}
        sx={{ px: 0, minHeight: 44 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {title} ({items.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0, pb: 1.5 }}>
        <Stack spacing={1.25}>
          {items.map((item, idx) => {
            const ids = itemPhotoIds(item);
            return (
              <Box key={`${title}-${idx}`}>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {item.text}
                </Typography>
                {ids.length ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mt: 0.75 }}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {ids.map((pid) => {
                      const photo = photosById.get(Number(pid));
                      if (!photo) {
                        return (
                          <Typography
                            key={pid}
                            variant="caption"
                            color="text.secondary"
                          >
                            #{pid}
                          </Typography>
                        );
                      }
                      const isVideo =
                        photo.kind === "video" ||
                        mediaKindFromMime(photo.mimeType) === "video";
                      const isPdf = isMeetingPdfPhoto(photo);
                      const open = () => onOpenMedia?.(Number(pid));
                      return (
                        <Box
                          key={pid}
                          component={onOpenMedia ? "button" : "div"}
                          type={onOpenMedia ? "button" : undefined}
                          onClick={onOpenMedia ? open : undefined}
                          sx={{
                            width: 80,
                            p: 0,
                            m: 0,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1.25,
                            bgcolor: "background.paper",
                            cursor: onOpenMedia ? "pointer" : "default",
                            textAlign: "left",
                            overflow: "hidden",
                          }}
                        >
                          {isVideo || isPdf ? (
                            <Box
                              sx={{
                                width: "100%",
                                height: 72,
                                bgcolor: isPdf ? "error.light" : "grey.900",
                                display: "grid",
                                placeItems: "center",
                                color: isPdf
                                  ? "error.contrastText"
                                  : "common.white",
                                fontWeight: 800,
                                fontSize: 12,
                              }}
                              aria-hidden
                            >
                              {isPdf ? "PDF" : "▶"}
                            </Box>
                          ) : (
                            <Box
                              component="img"
                              src={meetingMediaUrl(photo.fileUrl)}
                              alt=""
                              loading="lazy"
                              sx={{
                                width: "100%",
                                height: 72,
                                objectFit: "cover",
                                display: "block",
                                pointerEvents: "none",
                              }}
                            />
                          )}
                          <Typography
                            variant="caption"
                            color="primary"
                            display="block"
                            sx={{
                              px: 0.75,
                              py: 0.35,
                              fontWeight: 800,
                              lineHeight: 1.2,
                            }}
                          >
                            {isPdf ? "PDF" : isVideo ? "Vídeo" : "Foto"} #{pid}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                ) : null}
              </Box>
            );
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

/** Bloque imprimible del resumen. Solo lectura: no toca media.
 * compact=true: en pantalla solo blurb (+ PDF); el detalle vive en el feed.
 */
export default function MeetingSummaryPrint({
  session,
  summary,
  onOpenMedia,
  compact = false,
  summaryLang = "es",
  onSummaryLangChange,
  hasSummaryEn = false,
  hideActions = false,
  /** Solo documento de impresión (portal); sin UI en pantalla. */
  printOnly = false,
}) {
  const { t } = useTranslation();

  useEffect(() => {
    ensurePrintHostStyles();
  }, []);

  const photos = session?.photos || [];
  const photosById = useMemo(
    () => new Map(photos.map((p) => [Number(p.id), p])),
    [photos],
  );

  const transcript = String(
    summary?.transcript ||
      summary?.finalTranscript ||
      summary?.fullTranscript ||
      "",
  ).trim();

  const visualNotes = useMemo(() => {
    const notes = Array.isArray(summary?.visualNotes)
      ? summary.visualNotes
      : [];
    const videoish = [];
    const photoish = [];
    for (const n of notes) {
      const text = String(n?.text || "");
      const ids = itemPhotoIds(n);
      const linkedVideo = ids.some((id) => {
        const p = photosById.get(id);
        return (
          p && (p.kind === "video" || mediaKindFromMime(p.mimeType) === "video")
        );
      });
      if (linkedVideo || /^vídeo\s*#/i.test(text) || /\bvídeo\b/i.test(text)) {
        videoish.push(n);
      } else {
        photoish.push(n);
      }
    }
    return { videoish, photoish };
  }, [summary?.visualNotes, photosById]);

  const blurb = useMemo(
    () => shortMeetingBlurb(summary?.rawSummary, summary?.headline),
    [summary?.rawSummary, summary?.headline],
  );

  const fromVideos = useMemo(() => {
    if (Array.isArray(summary?.fromVideos) && summary.fromVideos.length) {
      return summary.fromVideos;
    }
    return extractVideoTranscriptSections(transcript);
  }, [summary?.fromVideos, transcript]);

  const hasGptSummary = Boolean(summary && summary.status !== "error");

  const mediaNotesForPrint = useMemo(() => {
    const map = notesByPhotoId(summary, transcript, {
      photos,
      includePhotoVisualNotes: true,
    });
    const audioMap = audioCouplingByPhotoId(photos, session?.audioChunks || []);
    const items = [];
    for (const photo of photos) {
      if (photo?.pendingUpload) continue;
      if (isMeetingPdfPhoto(photo)) continue;
      const id = Number(photo.id);
      if (!Number.isFinite(id)) continue;
      const bucket = map.get(id);
      const audioLink = audioMap.get(id);
      const isVideo =
        photo.kind === "video" || mediaKindFromMime(photo.mimeType) === "video";
      const vision = String(photo.visionText || "").trim();
      const pieceTranscript = String(photo.transcriptText || "").trim();
      const teamNote = String(photo.teamNote || "").trim();
      const caption = String(photo.caption || "").trim();
      const parts = [
        ...(bucket?.spoken || []),
        ...(!isVideo ? audioLink?.spoken || [] : []),
        ...(isVideo && pieceTranscript ? [pieceTranscript] : []),
        ...(!isVideo && vision ? [vision] : []),
        ...(bucket?.visual || []),
        ...(bucket?.linked || []),
      ].filter(Boolean);
      const unique = [];
      for (const p of parts) {
        if (p && !unique.includes(p)) unique.push(p);
      }
      const audioLabel =
        !isVideo && audioLink?.chunk?.seq != null
          ? ` · audio ${Number(audioLink.chunk.seq) + 1}`
          : "";
      const bodyParts = [];
      if (teamNote) bodyParts.push(teamNote);
      if (caption && caption !== teamNote) bodyParts.push(caption);
      if (unique.length) bodyParts.push(unique.join(" · "));
      if (!bodyParts.length) bodyParts.push(t("Meetings.mediaNoteEmpty"));
      items.push({
        text: `${isVideo ? "Vídeo" : "Foto"} #${id}${
          photo.offsetLabel ? ` · ${photo.offsetLabel}` : ""
        }${audioLabel}: ${bodyParts.join(" · ")}`,
        photoIds: [id],
      });
    }
    return items;
  }, [summary, transcript, photos, session?.audioChunks, t]);

  const freeNotesForPrint = useMemo(() => {
    const list = Array.isArray(session?.freeNotes) ? session.freeNotes : [];
    return list
      .map((note, idx) => {
        const plain = htmlToPlainText(note?.body || note?.text || "").trim();
        if (!plain) return null;
        const author = String(note?.username || "").trim();
        return {
          text: author ? `${author}: ${plain}` : plain,
          photoIds: [],
          _key: note?.id || idx,
        };
      })
      .filter(Boolean);
  }, [session?.freeNotes]);

  const audioChunksForPrint = useMemo(() => {
    const chunks = Array.isArray(session?.audioChunks)
      ? session.audioChunks
      : [];
    return chunks
      .map((chunk, idx) => {
        const text = String(chunk?.transcriptText || "").trim();
        if (!text) return null;
        return {
          text: `${t("Meetings.audioChunkLabel", {
            n: (chunk.seq ?? idx) + 1,
          })}: ${text}`,
          photoIds: [],
        };
      })
      .filter(Boolean);
  }, [session?.audioChunks, t]);

  const photoishNotes = visualNotes.photoish.length
    ? visualNotes.photoish
    : summary?.visualNotes;

  const printTitle =
    (hasGptSummary && summary.headline) ||
    session?.title ||
    t("Meetings.summary");
  const showScreenChrome = !printOnly && (hasGptSummary || !hideActions);

  const printDoc = (
    <Box id={PRINT_ROOT_ID} className="meeting-summary-print-root">
      <Box className="meeting-print-only mp-doc">
        <Box className="mp-brand">
          <Box component="span" className="mp-brand-mark">
            STAGE · {t("Meetings.summary")}
          </Box>
          <Box component="span" className="mp-brand-sub">
            {session?.meetingDate || ""}
          </Box>
        </Box>

        <Typography component="h1" className="mp-title">
          {printTitle}
        </Typography>

        <Box className="mp-meta">
          {session?.customerName ? (
            <Box className="mp-meta-row">
              <Box component="span" className="mp-meta-label">
                {t("Meetings.customer")}:
              </Box>
              {session.customerName}
            </Box>
          ) : null}
          {session?.meetingDate ? (
            <Box className="mp-meta-row">
              <Box component="span" className="mp-meta-label">
                {t("Meetings.date")}:
              </Box>
              {session.meetingDate}
            </Box>
          ) : null}
          {session?.attendeeNames ? (
            <Box className="mp-meta-row">
              <Box component="span" className="mp-meta-label">
                {t("Meetings.attendees")}:
              </Box>
              {session.attendeeNames}
            </Box>
          ) : null}
          {session?.clientContactNames ? (
            <Box className="mp-meta-row">
              <Box component="span" className="mp-meta-label">
                {t("Meetings.clientAttendees")}:
              </Box>
              {session.clientContactNames}
            </Box>
          ) : null}
          <Box className="mp-meta-row">
            <Box component="span" className="mp-meta-label">
              Media:
            </Box>
            {t("Meetings.counts", {
              audio: session?.audioCount || 0,
              photos: session?.imageCount ?? session?.photoCount ?? 0,
              videos: session?.videoCount || 0,
            })}
          </Box>
        </Box>

        {blurb ? (
          <Typography component="div" className="mp-raw">
            {blurb}
          </Typography>
        ) : null}

        <FlatSection
          title={t("Meetings.freeNotesTitle")}
          items={freeNotesForPrint}
        />
        <FlatSection
          title={t("Meetings.mediaNotesPrint")}
          items={mediaNotesForPrint}
          photosById={photosById}
          showThumbs
        />
        <FlatSection
          title={t("Meetings.audioChunksTitle")}
          items={audioChunksForPrint}
        />
        {hasGptSummary ? (
          <>
            <FlatSection
              title={t("Meetings.forDesign")}
              items={summary.forDesign}
              photosById={photosById}
              showThumbs
            />
            <FlatSection
              title={t("Meetings.forSales")}
              items={summary.forSales}
              photosById={photosById}
              showThumbs
            />
            <FlatSection
              title={t("Meetings.decisions")}
              items={summary.decisions}
              photosById={photosById}
              showThumbs
            />
            <FlatSection
              title={t("Meetings.openQuestions")}
              items={summary.openQuestions}
              photosById={photosById}
              showThumbs
            />
            <FlatSection
              title={t("Meetings.fromVideos")}
              items={fromVideos}
              photosById={photosById}
              showThumbs
            />
          </>
        ) : null}

        {!freeNotesForPrint.length &&
        !mediaNotesForPrint.length &&
        !audioChunksForPrint.length &&
        !hasGptSummary ? (
          <Typography component="div" className="mp-raw">
            {t("Meetings.summaryPrintEmpty")}
          </Typography>
        ) : null}

        <Box className="mp-footer">
          <span>STAGE Design · CRM Reuniones</span>
          <span>
            {[session?.title, printTitle].filter(Boolean).join(" · ")}
          </span>
        </Box>
      </Box>
    </Box>
  );

  const printPortal =
    typeof document !== "undefined"
      ? createPortal(printDoc, document.body)
      : null;

  if (!showScreenChrome) {
    return printPortal;
  }

  return (
    <>
      <Box className="meeting-summary-print-screen">
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={1.25}
          sx={{ mb: 1.5 }}
          className="meeting-print-actions meeting-screen-only"
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, wordBreak: "break-word", lineHeight: 1.3 }}
          >
            {printTitle}
          </Typography>
          {!hideActions ? (
            <Stack direction="row" spacing={1} alignItems="center">
              {hasGptSummary &&
              (hasSummaryEn || typeof onSummaryLangChange === "function") ? (
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={summaryLang}
                  onChange={(_, next) => {
                    if (!next || typeof onSummaryLangChange !== "function")
                      return;
                    onSummaryLangChange(next);
                  }}
                  className="meeting-no-print"
                >
                  <ToggleButton value="es" aria-label="ES">
                    ES
                  </ToggleButton>
                  <Tooltip
                    title={
                      hasSummaryEn
                        ? t("Meetings.summaryLangEn")
                        : t("Meetings.summaryLangHint")
                    }
                  >
                    <span>
                      <ToggleButton
                        value="en"
                        aria-label="EN"
                        disabled={!hasSummaryEn}
                      >
                        EN
                      </ToggleButton>
                    </span>
                  </Tooltip>
                </ToggleButtonGroup>
              ) : null}
              <Button
                variant="outlined"
                size="small"
                startIcon={<PrintOutlinedIcon />}
                onClick={printMeetingSummary}
                sx={{
                  alignSelf: { xs: "stretch", sm: "center" },
                  minHeight: 40,
                }}
              >
                {t("Meetings.printExport")}
              </Button>
            </Stack>
          ) : null}
        </Stack>

        <Box className="meeting-print-meta meeting-screen-only" sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.45, wordBreak: "break-word" }}
          >
            {[
              session?.title,
              session?.customerName
                ? `${t("Meetings.customer")}: ${session.customerName}`
                : null,
              session?.meetingDate
                ? `${t("Meetings.date")}: ${session.meetingDate}`
                : null,
              session?.attendeeNames
                ? `${t("Meetings.attendees")}: ${session.attendeeNames}`
                : null,
              session?.clientContactNames
                ? `${t("Meetings.clientAttendees")}: ${session.clientContactNames}`
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {t("Meetings.counts", {
              audio: session?.audioCount || 0,
              photos: session?.imageCount ?? session?.photoCount ?? 0,
              videos: session?.videoCount || 0,
            })}
          </Typography>
        </Box>

        {blurb ? (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              whiteSpace: "pre-wrap",
              lineHeight: 1.5,
              maxWidth: 72 * 8,
            }}
            className="meeting-print-raw meeting-screen-only"
          >
            {blurb}
          </Typography>
        ) : null}

        <Box className="meeting-screen-only">
          {!compact && hasGptSummary ? (
            <>
              <PrintSection
                title={t("Meetings.fromVideos")}
                items={fromVideos}
                photosById={photosById}
                onOpenMedia={onOpenMedia}
              />
              <PrintSection
                title={t("Meetings.videoVisualNotes")}
                items={visualNotes.videoish}
                photosById={photosById}
                onOpenMedia={onOpenMedia}
              />
              <PrintSection
                title={t("Meetings.visualNotes")}
                items={photoishNotes}
                photosById={photosById}
                onOpenMedia={onOpenMedia}
                defaultExpanded={!visualNotes.videoish.length}
              />
              <PrintSection
                title={t("Meetings.forDesign")}
                items={summary.forDesign}
                photosById={photosById}
                onOpenMedia={onOpenMedia}
              />
              <PrintSection
                title={t("Meetings.forSales")}
                items={summary.forSales}
                photosById={photosById}
                onOpenMedia={onOpenMedia}
              />
              <PrintSection
                title={t("Meetings.decisions")}
                items={summary.decisions}
                photosById={photosById}
                onOpenMedia={onOpenMedia}
              />
              <PrintSection
                title={t("Meetings.openQuestions")}
                items={summary.openQuestions}
                photosById={photosById}
                onOpenMedia={onOpenMedia}
              />
            </>
          ) : null}
        </Box>
      </Box>
      {printPortal}
    </>
  );
}
